import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsLandscape } from '../hooks/useIsLandscape';
import {
  FONT_CATALOG,
  type FontCategory,
  type FontEntry,
  filterFonts,
  getFontEntry,
  ensureGoogleFontLoaded,
  loadRecentFonts,
  recordRecentFont,
} from '../lib/google-fonts';

interface GoogleFontsPickerProps {
  value: string;
  onChange: (fontFamily: string) => void;
}

const CATEGORY_CHIPS: { id: FontCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'sans', label: 'Sans' },
  { id: 'serif', label: 'Serif' },
  { id: 'display', label: 'Display' },
  { id: 'mono', label: 'Mono' },
  { id: 'handwriting', label: 'Hand' },
];

/** Google Fonts picker — Tier 2 Group B. Replaces the native font
 *  dropdown in TextPanel. Each row renders in its own face so the
 *  user sees what they're picking; selection lazy-loads the
 *  @font-face. Mobile (portrait) opens a full-height bottom sheet
 *  instead of a popover.
 *
 *  Recent fonts persist per-user via localStorage and surface above
 *  the search results when no query is entered. */
export function GoogleFontsPicker({ value, onChange }: GoogleFontsPickerProps) {
  const [open, setOpen] = useState(false);
  const isLandscape = useIsLandscape();
  const currentEntry = getFontEntry(value);
  const triggerLabel = currentEntry?.label ?? value;
  const triggerPreview = currentEntry?.preview ?? value;

  // Eagerly request the @font-face for the current selection on
  // first mount so the active layer renders correctly without
  // requiring the user to re-pick the font after page reload.
  useEffect(() => {
    ensureGoogleFontLoaded(value);
  }, [value]);

  const trigger = (
    <button
      type="button"
      className="flex-1 h-8 px-2 inline-flex items-center justify-between gap-2 text-xs bg-background border border-border rounded hover:bg-accent transition-colors"
      onClick={() => setOpen((v) => !v)}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      <span className="truncate" style={{ fontFamily: triggerPreview }}>
        {triggerLabel}
      </span>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
    </button>
  );

  const handlePick = (entry: FontEntry) => {
    ensureGoogleFontLoaded(entry.value);
    recordRecentFont(entry.value);
    onChange(entry.value);
    setOpen(false);
  };

  if (isLandscape) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <PickerBody currentValue={value} onPick={handlePick} maxHeight="60vh" />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      {trigger}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-lg max-h-[85vh] flex flex-col p-0">
          <SheetHeader className="px-4 pt-4 pb-2 shrink-0">
            <SheetTitle>Font</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <PickerBody currentValue={value} onPick={handlePick} maxHeight="100%" />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function PickerBody({
  currentValue,
  onPick,
  maxHeight,
}: {
  currentValue: string;
  onPick: (entry: FontEntry) => void;
  maxHeight: string;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<FontCategory | 'all'>('all');
  const [recents, setRecents] = useState<string[]>(() => loadRecentFonts());
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Refresh recents when the popover/sheet (re-)opens — the
    // picker can be mounted across multiple TextPanel instances.
    setRecents(loadRecentFonts());
  }, []);

  const results = useMemo(() => filterFonts(FONT_CATALOG, query, category), [query, category]);

  // Recent-font rows always render against the catalog (we may
  // have dropped some entries between versions — those vanish from
  // the recents view silently).
  const recentEntries = useMemo(() => {
    if (query.trim() || category !== 'all') return [];
    return recents.map((v) => getFontEntry(v)).filter((e): e is FontEntry => Boolean(e));
  }, [recents, query, category]);

  return (
    <div className="flex flex-col" style={{ maxHeight }}>
      <div className="px-3 pt-3 pb-2 space-y-2 shrink-0 border-b border-border">
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search fonts"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full h-8 pl-7 pr-2 text-xs bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORY_CHIPS.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={() => setCategory(chip.id)}
              className={`px-2 py-0.5 text-[11px] rounded ${
                category === chip.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-foreground hover:bg-accent/70'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {recentEntries.length > 0 ? (
          <div className="px-2 pt-2">
            <h5 className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wide mb-1 px-1">
              Recent
            </h5>
            <ul className="space-y-0.5 mb-2">
              {recentEntries.map((entry) => (
                <FontRow
                  key={`recent_${entry.value}`}
                  entry={entry}
                  active={entry.value === currentValue}
                  onPick={onPick}
                />
              ))}
            </ul>
          </div>
        ) : null}
        <ul className="px-2 py-2 space-y-0.5">
          {results.length === 0 ? (
            <li className="px-2 py-3 text-xs text-muted-foreground text-center">No fonts match</li>
          ) : (
            results.map((entry) => (
              <FontRow
                key={entry.value}
                entry={entry}
                active={entry.value === currentValue}
                onPick={onPick}
              />
            ))
          )}
        </ul>
      </div>
    </div>
  );
}

function FontRow({
  entry,
  active,
  onPick,
}: {
  entry: FontEntry;
  active: boolean;
  onPick: (entry: FontEntry) => void;
}) {
  // Eagerly request the @font-face when the row mounts so the
  // user sees the real font as they scroll, rather than only after
  // selection.
  useEffect(() => {
    ensureGoogleFontLoaded(entry.value);
  }, [entry.value]);

  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(entry)}
        className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded min-h-[36px] text-left transition-colors ${
          active
            ? 'bg-primary/10 text-foreground'
            : 'text-foreground hover:bg-accent cursor-pointer'
        }`}
        style={{ fontFamily: entry.preview }}
      >
        <span className="truncate">{entry.label}</span>
      </button>
    </li>
  );
}
